import React, {Suspense, useEffect} from 'react';

import classnames from 'classnames';
import appActions from 'client/AppActions';
import {callAction} from 'client/framework';
import LoadingIndicator from 'generic/LoadingIndicator.react';

import {Sidemenu} from '../Sidemenu/Sidemenu';

const footerActions = appActions as Record<'SET_FOOTER_DISPLAY', symbol>;

interface Props {
  children: React.ReactNode;
  /** Page header above the scrollable content (class banner / All Classwork header). */
  header?: React.ReactNode;
  pathname: string;
  /**
   * Optional right rail (Home's Get Started module): flush beside the content on wide screens, an
   * overlay over the content on narrow ones. Renders after the content pane.
   */
  rightRail?: React.ReactNode;
}

/**
 * Shared v2 dashboard chrome: persistent sidemenu + optional page header + optional right rail. The
 * content pane has its own Suspense boundary so a lazy tab loads only the pane — the sidemenu, header,
 * and rail stay put. The right rail (when provided) owns its own chrome and width.
 */
export const TeacherDashboardLayout = ({children, header, pathname, rightRail}: Props) => {
  useEffect(() => {
    callAction(footerActions.SET_FOOTER_DISPLAY, false);

    // The dashboard fills the viewport and scrolls inside <main>, so the document itself must never
    // scroll. Otherwise a sidebar tooltip (portaled to <body>) opening below the fold nudges
    // html.scrollHeight just past the viewport, flickering the document scrollbar on/off — which
    // reflows the whole layout by the scrollbar's width on every hover.
    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = 'hidden';

    return () => {
      callAction(footerActions.SET_FOOTER_DISPLAY, true);
      root.style.overflow = previousOverflow;
    };
  }, []);

  return (
    <div className='-mt-[60px] flex h-[calc(100vh-60px)] background-subtle'>
      <Sidemenu pathname={pathname} />
      {/* Clip the Get Started rail: its overlay slides off-screen via translate when closed, which
          would otherwise add a horizontal scrollbar. Applied whenever a page provides a rail
          (currently Home); rail-less pages keep visible overflow. */}
      <div
        className={classnames('min-w-0 relative flex grow', {
          'overflow-hidden': Boolean(rightRail)
        })}
      >
        {header ? (
          <main className='min-w-0 flex grow flex-col overflow-y-auto background-primary'>
            {header}
            <div className='grow px-x4 pb-x4'>
              <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
            </div>
          </main>
        ) : (
          <main className='min-w-0 grow overflow-y-auto background-subtle'>
            <div className='p-x5'>
              <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
            </div>
          </main>
        )}
        {rightRail}
      </div>
    </div>
  );
};
